import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  DestroyRef,
} from "@angular/core";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { QRCodeComponent } from "angularx-qrcode";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faDownload, faMobileAlt } from "@fortawesome/free-solid-svg-icons";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-student-meetday-qr",
  standalone: true,
  imports: [CommonModule, TranslateModule, QRCodeComponent, FontAwesomeModule],
  template: `
    <div class="w-100" *ngIf="qrCodeUrl">
      <div class="card border-0 shadow-sm">
        <div class="card-body p-4 text-center">
          <!-- QR Code Display -->
          <div class="d-flex justify-content-center mb-3">
            <div id="qrcode" class="p-3 bg-white rounded border shadow-sm">
              <qrcode
                [qrdata]="qrCodeUrl"
                [width]="160"
                [errorCorrectionLevel]="'M'"
                [colorDark]="'#2c3e50'"
                [colorLight]="'#ffffff'"
                [margin]="1"
              >
              </qrcode>
            </div>
          </div>

          <!-- Instructions -->
          <div class="mb-3">
            <small
              class="text-muted d-flex align-items-center justify-content-center"
            >
              {{
                "Show this QR code to companies at the meetday to confirm your attendance"
                  | translate
              }}
            </small>
          </div>

          <!-- Download Button -->
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            (click)="downloadQRCode()"
          >
            <fa-icon [icon]="faDownload" class="me-2"></fa-icon>
            {{ "Download QR Code" | translate }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div class="text-center" *ngIf="!qrCodeUrl">
      <div class="card border-0 shadow-sm">
        <div class="card-body p-4">
          <div class="d-flex justify-content-center align-items-center">
            <div
              class="spinner-border spinner-border-sm text-primary me-2"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
            <small class="text-muted">{{
              "Generating your QR code..." | translate
            }}</small>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class StudentMeetdayQrComponent implements OnInit, OnChanges {
  @Input() meetDayId: string = "";
  @Input() studentId: string | null = null;

  qrCodeUrl: string = "";
  private resolvedStudentId: string = "";

  // FontAwesome icons
  faDownload = faDownload;
  faMobileAlt = faMobileAlt;

  constructor(
    private authService: AuthenticationService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    if (this.studentId) {
      this.resolvedStudentId = this.studentId;
      this.generateQRCodeUrl();
      return;
    }

    this.authService.studentprofile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        if (profile?.id) {
          this.resolvedStudentId = profile.id;
          this.generateQRCodeUrl();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["studentId"]) {
      const updatedId = changes["studentId"].currentValue;
      if (updatedId) {
        this.resolvedStudentId = updatedId;
      }
    }

    if (this.meetDayId && this.resolvedStudentId) {
      this.generateQRCodeUrl();
    }
  }

  private generateQRCodeUrl(): void {
    if (this.meetDayId && this.resolvedStudentId) {
      // Generate the URL that companies will scan
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      this.qrCodeUrl = `${baseUrl}/meetday-attendance/${this.meetDayId}?studentId=${this.resolvedStudentId}`;
    }
  }

  downloadQRCode(): void {
    const canvas = document.querySelector(
      "#qrcode canvas"
    ) as HTMLCanvasElement;
    if (!canvas) {
      alert("QR Code not yet generated. Please wait a moment and try again.");
      return;
    }

    try {
      const link = document.createElement("a");
      link.download = `meetday-qr-${this.meetDayId}.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading QR code:", error);
      alert("Error downloading QR code. Please try again.");
    }
  }
}
